import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import './styles.css';
import { SharePage } from './pages/share-page';
import { SharedChatPage } from './pages/shared-chat-page';
import { ChatPage } from './pages/chat-page';
import { BookPage } from './pages/book-page';
import { DiscordRedirectPage } from './pages/discord-redirect-page';
import {
  LegacyDiscordSettingsPage,
  LegacyTelegramSettingsPage,
  legacyConnectRoutes,
} from './legacy-connect';
import {
  SettingsBillingCreditsPage,
  SettingsBillingPlansPage,
  SettingsBillingRedeemPage,
  SettingsComputersPage,
} from './pages/settings-account-pages';
import { LoginPage } from './pages/login-page';
import { AgentSessionDebugPage } from './pages/agent-session-debug-page';
import { OnboardingCheckoutReturnPage } from './pages/onboarding-checkout-return-page';
import { OnboardingDownloadPage } from './pages/onboarding-download-page';
import { isMacOSOnboardingRuntime } from './components/onboarding/onboarding-native-runtime';
import { resolveWebOnboardingDownloadPath } from './lib/onboarding';
import { WebsiteThemeProvider } from './lib/theme';
import { WebsiteTitlebarProvider } from './lib/titlebar';
import { Toaster } from './components/ui/sonner';
import { initCueboardPostHog } from '@/utils/posthog';

const ProductOnboardingGate = React.lazy(() =>
  import('./components/onboarding/product-onboarding-gate').then(module => ({
    default: module.ProductOnboardingGate,
  }))
);
const OnboardingChatPage = React.lazy(() =>
  import('./components/onboarding/onboarding-chat-page').then(module => ({
    default: module.OnboardingChatPage,
  }))
);
const OnboardingWaitlistPage = React.lazy(() =>
  import('./components/onboarding/onboarding-waitlist-page').then(module => ({
    default: module.OnboardingWaitlistPage,
  }))
);

function requireCompletedOnboarding(element: React.ReactElement) {
  return lazyRoute(<ProductOnboardingGate>{element}</ProductOnboardingGate>);
}

function NativeOnboardingOnly({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (!isMacOSOnboardingRuntime(searchParams)) {
    return (
      <Navigate
        to={resolveWebOnboardingDownloadPath(searchParams.get('next'))}
        replace
        state={{ from: location }}
      />
    );
  }

  return children;
}

function LegacyAgentConversationRedirect() {
  const { conversationId } = useParams();
  const location = useLocation();
  const target = conversationId
    ? `/chat/${encodeURIComponent(conversationId)}${location.search}`
    : `/chat${location.search}`;

  return <Navigate to={target} replace />;
}

function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation();
  if (!location.search) {
    return <Navigate to={to} replace />;
  }
  const separator = to.includes('?') ? '&' : '?';
  return (
    <Navigate to={`${to}${separator}${location.search.slice(1)}`} replace />
  );
}

function BillingSettingsRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const target =
    searchParams.get('redeem') === '1'
      ? '/settings/billing/redeem'
      : searchParams.has('checkout')
        ? '/settings/billing/plans'
        : '/settings/billing/credits';

  return <Navigate to={`${target}${location.search}`} replace />;
}

function SettingsRouteOutlet() {
  return <Outlet />;
}

function isDarkOnboardingPath(pathname: string) {
  return (
    pathname === '/onboarding' ||
    pathname === '/onboarding/offer' ||
    pathname === '/onboarding/checkout/return'
  );
}

function lazyRoute(
  element: React.ReactElement,
  options?: { darkFallback?: boolean }
) {
  return (
    <React.Suspense
      fallback={
        <div
          className={
            options?.darkFallback
              ? 'flex h-[100dvh] items-center justify-center overflow-hidden bg-black text-white/58'
              : 'flex h-[100dvh] items-center justify-center overflow-hidden bg-background text-muted-foreground'
          }
        >
          Loading
        </div>
      }
    >
      {element}
    </React.Suspense>
  );
}

function WebsiteRoutes() {
  const location = useLocation();
  const isDarkOnboarding = isDarkOnboardingPath(location.pathname);

  return (
    <div
      className={
        isDarkOnboarding
          ? 'h-full overflow-hidden bg-black text-white'
          : 'h-full overflow-hidden bg-background text-foreground'
      }
    >
      <Routes>
        <Route path="/" element={<Navigate to="/chat" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/onboarding"
          element={lazyRoute(
            <NativeOnboardingOnly>
              <OnboardingChatPage />
            </NativeOnboardingOnly>,
            { darkFallback: true }
          )}
        />
        <Route
          path="/onboarding/waitlist"
          element={lazyRoute(<OnboardingWaitlistPage />)}
        />
        <Route
          path="/onboarding/offer"
          element={lazyRoute(
            <NativeOnboardingOnly>
              <OnboardingChatPage />
            </NativeOnboardingOnly>,
            { darkFallback: true }
          )}
        />
        <Route
          path="/onboarding/download"
          element={<OnboardingDownloadPage />}
        />
        <Route
          path="/onboarding/checkout/return"
          element={<OnboardingCheckoutReturnPage />}
        />
        <Route
          path="/chat"
          element={requireCompletedOnboarding(<ChatPage />)}
        />
        <Route
          path="/chat/:conversationId"
          element={requireCompletedOnboarding(<ChatPage />)}
        />
        <Route
          path="/agent-conversations/:rootConversationId/:conversationId"
          element={requireCompletedOnboarding(
            <LegacyAgentConversationRedirect />
          )}
        />
        <Route
          path="/debug/agents/:agentId/sessions/:sessionId"
          element={requireCompletedOnboarding(<AgentSessionDebugPage />)}
        />
        <Route
          path="/billing"
          element={requireCompletedOnboarding(<BillingSettingsRedirect />)}
        />
        <Route
          path="/book"
          element={requireCompletedOnboarding(<BookPage />)}
        />
        <Route
          path="/computers"
          element={requireCompletedOnboarding(
            <RedirectWithSearch to="/settings/computers" />
          )}
        />
        <Route
          path="/settings"
          element={requireCompletedOnboarding(<SettingsRouteOutlet />)}
        >
          <Route
            index
            element={<RedirectWithSearch to="/settings/integrations/discord" />}
          />
          <Route path="billing" element={<BillingSettingsRedirect />} />
          <Route
            path="billing/credits"
            element={<SettingsBillingCreditsPage />}
          />
          <Route path="billing/plans" element={<SettingsBillingPlansPage />} />
          <Route
            path="billing/redeem"
            element={<SettingsBillingRedeemPage />}
          />
          <Route path="computers" element={<SettingsComputersPage />} />
          <Route
            path="integrations/slack"
            element={<RedirectWithSearch to="/settings/integrations/discord" />}
          />
          <Route
            path="integrations/wechat"
            element={<RedirectWithSearch to="/settings/integrations/discord" />}
          />
          <Route
            path="integrations/telegram"
            element={<LegacyTelegramSettingsPage />}
          />
          <Route
            path="integrations/feishu"
            element={<RedirectWithSearch to="/settings/integrations/discord" />}
          />
          <Route
            path="integrations/legacy"
            element={<RedirectWithSearch to="/settings/integrations/discord" />}
          />
          <Route
            path="integrations/discord"
            element={<LegacyDiscordSettingsPage />}
          />
        </Route>
        <Route
          path="/workspace-bots"
          element={requireCompletedOnboarding(
            <RedirectWithSearch to="/settings/integrations/discord" />
          )}
        />
        <Route
          path="/workspace-bots/slack"
          element={requireCompletedOnboarding(
            <RedirectWithSearch to="/settings/integrations/discord" />
          )}
        />
        {legacyConnectRoutes({
          requireCompletedOnboarding,
          RedirectWithSearch,
        })}
        <Route path="/redirect/discord" element={<DiscordRedirectPage />} />
        <Route path="/c/:publicId" element={<SharedChatPage />} />
        <Route path="/s/:agentId/:shareId" element={<SharePage />} />
        <Route path="/s/:publicId" element={<SharePage />} />
        <Route
          path="*"
          element={
            <div className="flex h-[100dvh] items-center justify-center overflow-hidden p-8 text-muted-foreground">
              Page not found
            </div>
          }
        />
      </Routes>
    </div>
  );
}

const App: React.FC = () => {
  useEffect(() => {
    initCueboardPostHog({
      surface: 'website',
      runtime: 'browser',
    });
  }, []);

  return (
    <WebsiteThemeProvider>
      <WebsiteTitlebarProvider>
        <Router>
          <WebsiteRoutes />
        </Router>
        <Toaster />
      </WebsiteTitlebarProvider>
    </WebsiteThemeProvider>
  );
};

export default App;
